import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { getCurrentTimezone } from '@/lib/utils/timezone';
import {
  getEnvironment,
  invalidateEnvironmentCache,
  type CurrentUser,
  type GetEnvironmentResponse,
} from '@/lib/api-common/envApi';

export interface EnvsState {
  currentUser: CurrentUser | null;
  timezone: string;
  advertisingTypes: unknown[];
  campaignGenres: unknown[];
  adminsEmails: string[];
  admins: Record<string, unknown>;
  templates: unknown[];
  env: string;
  organization: unknown;
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: EnvsState = {
  currentUser: null,
  timezone: getCurrentTimezone(),
  advertisingTypes: [],
  campaignGenres: [],
  adminsEmails: [],
  admins: {},
  templates: [],
  env: 'development',
  organization: null,
  isLoaded: false,
  isLoading: false,
  error: null,
};

export const fetchEnvironment = createAsyncThunk<
  GetEnvironmentResponse,
  { forceRefresh?: boolean } | void,
  { rejectValue: { message: string } }
>(
  'envs/fetchEnvironment',
  async (arg, { rejectWithValue }) => {
    const forceRefresh =
      arg != null &&
      typeof arg === 'object' &&
      'forceRefresh' in arg &&
      arg.forceRefresh === true;
    try {
      return await getEnvironment({ forceRefresh });
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message ?? 'Failed to load environment';
      return rejectWithValue({ message: msg });
    }
  }
);

const envsSlice = createSlice({
  name: 'envs',
  initialState,
  reducers: {
    setCurrentUser: (state, action: PayloadAction<CurrentUser | null>) => {
      state.currentUser = action.payload;
    },
    setTimezone: (state, action: PayloadAction<string>) => {
      state.timezone = action.payload;
    },
    clearEnv: () => {
      invalidateEnvironmentCache();
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEnvironment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEnvironment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoaded = true;
        state.error = null;

        // 미인증(401/302)은 envApi가 GUEST_RESPONSE 센티넬(`id: 'guest'`)로 반환한다.
        // 실제 admin/advertiser 유저는 절대 id가 'guest'가 아니므로 id로만 판별한다.
        // (센티넬에 role: 'guest'가 붙어 있어 과거의 `!user.role` 조건이 미인증을
        //  인증으로 오판 → guest가 대시보드에 진입/토큰 로그인 스킵되는 버그였다.)
        const user = action.payload.currentUser;
        const isUnauthenticated = !user || user.id === 'guest';
        state.currentUser = isUnauthenticated ? null : user;

        state.advertisingTypes = action.payload.advertisingTypes ?? [];
        state.campaignGenres = action.payload.campaignGenres ?? [];
        state.adminsEmails = action.payload.adminsEmails ?? [];
        state.admins = (action.payload.admins as Record<string, unknown>) ?? {};
        state.templates = action.payload.templates ?? [];
        state.env = action.payload.env ?? 'development';
        state.organization = action.payload.organization ?? null;
      })
      .addCase(fetchEnvironment.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoaded = true;
        state.error = action.payload?.message ?? 'Unknown error';
        // 일시적 서버 오류(503 등)에서 currentUser를 초기화하지 않음
        // 미인증 상태(세션 만료)는 fulfilled에서 GUEST_RESPONSE로 감지
        if (import.meta.env.DEV) {
          console.warn('[auth] fetchEnvironment rejected (transient error, currentUser preserved):', {
            error: action.payload?.message,
            hadUser: !!state.currentUser,
            time: new Date().toISOString(),
          });
        }
      });
  },
});

export const { setCurrentUser, setTimezone, clearEnv } = envsSlice.actions;
export default envsSlice.reducer;
